import { PropsWithChildren, createContext, useContext, useState } from 'react'

import { MediaRecorderProvider } from '@verbit-ai/verbit-connect-web/recorder-provider'
import { MediaPlayerProvider } from '@verbit-ai/verbit-connect-web/player-provider'
import { BackupRecordingProvider } from '@verbit-ai/verbit-connect-web/backup-recording-provider'
import { AppState } from '@verbit-ai/verbit-connect-web/models'

import { useIsJobFinished, useIsRecordingStarted, useTranscriptMetadata } from 'src/contexts/Transcript/MetadataContext'
import { getMixpanelApiKey, getRollbarToken } from 'src/utils/env'
import { isUserWithRoles } from 'src/models/User'
import { ROLES_THAT_CAN_GO_OFF_RECORD, useVerbitConnectAvailability } from 'src/hooks/useVerbitConnectAvailability'
import { useShallowMemo } from 'src/hooks/useShallowMemo'

import { useOrderDetails } from './OrderDetailsContext'
import { useUserContext } from './UserProvider'
import { useFeatureFlag } from './FeatureFlagContext'

type VerbitConnectWrapperData = {
    isMediaSourceViewMode: boolean
}

const VerbitConnectWrapperContext = createContext<VerbitConnectWrapperData>({
    isMediaSourceViewMode: true,
})

type InnerWrapperProps = PropsWithChildren<{
    isAvailable: boolean
    orderId?: string
}>
type VerbitConnectWrapperProps = PropsWithChildren<{}>

const mixpanelToken = getMixpanelApiKey()
const rollbarToken = getRollbarToken()

const MediaRecorderWrapper = (props: InnerWrapperProps) => {
    const { isAvailable, orderId, children } = props
    const { order } = useOrderDetails()
    const isBackupRecordingAvailable = useFeatureFlag('backup_recording')
    const isOrderDetailsPollingOn = useFeatureFlag('order_details_polling')
    const isJobFinished = useIsJobFinished()
    const isRecordingStarted = useIsRecordingStarted()
    const [isLocallyRecording, setIsLocallyRecording] = useState(false)

    const isSessionEnding = isJobFinished && !isRecordingStarted && !isLocallyRecording

    return isAvailable ? (
        <MediaRecorderProvider
            orderId={orderId || ''}
            orderStatus={order?.status ?? null}
            mixpanelToken={mixpanelToken}
            rollbarToken={rollbarToken}
            canDisplayPermissionsWarning
            isBackupRecordingOn={isBackupRecordingAvailable}
            isSessionEnding={isSessionEnding}
            isOrderStatusCheckOn={isOrderDetailsPollingOn}
            onRecordStateChange={(state) => {
                setIsLocallyRecording(state === AppState.Streaming || state === AppState.Loading)
            }}>
            {children}
        </MediaRecorderProvider>
    ) : (
        <>{children}</>
    )
}

const MediaPlayerWrapper = (props: InnerWrapperProps) => {
    const { isAvailable, orderId, children } = props
    const preferLocalAudio = useFeatureFlag('prefer_local_audio')

    return isAvailable ? (
        <MediaPlayerProvider
            orderId={orderId || ''}
            mixpanelToken={mixpanelToken}
            rollbarToken={rollbarToken}
            preferLocalAudio={preferLocalAudio}>
            {children}
        </MediaPlayerProvider>
    ) : (
        <>{children}</>
    )
}

const BackupRecordingWrapper = (props: InnerWrapperProps) => {
    const { isAvailable, orderId, children } = props

    return isAvailable ? (
        <BackupRecordingProvider orderId={orderId || ''} mixpanelToken={mixpanelToken} rollbarToken={rollbarToken}>
            {children}
        </BackupRecordingProvider>
    ) : (
        <>{children}</>
    )
}

export const VerbitConnectWrapperProvider = (props: VerbitConnectWrapperProps) => {
    const { children } = props
    const metadata = useTranscriptMetadata()
    const { user } = useUserContext()
    const { isMediaRecorderAvailable, isMediaPlayerAvailable } = useVerbitConnectAvailability()

    const canUserGoOffRecord = isUserWithRoles(user, ROLES_THAT_CAN_GO_OFF_RECORD)
    const isMediaSourceViewMode = !canUserGoOffRecord || metadata?.inputType !== 'web_url'

    // this is not about backup recording feature as a whole - this is specifically about
    // backup recording context, which should be always available, so that we can use it
    // inside `useDownloadOptions`
    const isBackupRecordingAvailable = true

    return (
        <VerbitConnectWrapperContext.Provider value={useShallowMemo({ isMediaSourceViewMode })}>
            <MediaRecorderWrapper isAvailable={isMediaRecorderAvailable} orderId={metadata?.orderId}>
                <MediaPlayerWrapper isAvailable={isMediaPlayerAvailable} orderId={metadata?.orderId}>
                    <BackupRecordingWrapper isAvailable={isBackupRecordingAvailable} orderId={metadata?.orderId}>
                        {children}
                    </BackupRecordingWrapper>
                </MediaPlayerWrapper>
            </MediaRecorderWrapper>
        </VerbitConnectWrapperContext.Provider>
    )
}

export const useVerbitConnect = () => {
    return useContext(VerbitConnectWrapperContext)
}
